import { Plan, ProductType, useUpdatePriceForCountryAndLink } from '@app/api';
import { Theme } from '@app/theme';
import { toEuros } from 'i18n';
import { NO_PRICE_GROUP, sumProducts } from 'lib/priceComputation';
import { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { SquareButton, useBoolModalState, useToast } from '@app/core';

interface Props {
  plan: Plan;
  duration?: number;
  sessionLinkId?: string;
  groupId: string;
  type: ProductType;
  incentive?: number;
}

const useStyles = createUseStyles((theme: Theme) => ({
  cellContent: {
    display: 'flex',
    gap: theme.marginBase / 2,
    textAlign: 'left',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: theme.marginBase,

    '& .edit-button': {
      opacity: 0,
      transition: 'opacity .2s',
    },
    '&:hover': {
      backgroundColor: theme.hoverColors.white,
      '& .edit-button': {
        opacity: 1,
      },
    },
  },
  cellEmpty: {
    color: theme.colors.inkTertiary,
  },
  icon: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.blueAccessible,
  },
  editCell: {
    display: 'flex',
    paddingLeft: theme.marginBase,
    paddingRight: theme.marginBase,
    gap: theme.marginBase / 2,
  },
  input: {
    height: '100%',
    borderWidth: 0,
    maxWidth: 100,
    paddingTop: theme.marginBase,
    paddingBottom: theme.marginBase,
  },
}));

export const PriceEditionCountryCell = ({
  plan,
  sessionLinkId,
  groupId,
  type,
  incentive,
  duration,
}: Props) => {
  const classes = useStyles();
  const toast = useToast();

  const [isEditting, startEditting, stopEdit] = useBoolModalState();
  const [value, setValue] = useState<number | undefined>(undefined);
  const { mutateAsync: updatePrice } = useUpdatePriceForCountryAndLink(plan.id);

  const productsAndPrices = useMemo(() => {
    const products = plan?.products.filter((planProduct) => {
      return (
        (!planProduct.countryPriceGroupId ||
          planProduct.countryPriceGroupId === groupId) &&
        (planProduct.sessionLinkId || null) === (sessionLinkId || null) &&
        planProduct.product?.type === type
      );
    });

    const amount = sumProducts(products, 1, duration, incentive);
    return { products, amount };
  }, [plan, groupId, incentive, type, sessionLinkId]);

  const defaultPrice = useMemo(() => {
    if (!sessionLinkId) {
      return productsAndPrices;
    }
    const products = plan?.products.filter((planProduct) => {
      return (
        (!planProduct.countryPriceGroupId ||
          planProduct.countryPriceGroupId === groupId) &&
        !planProduct.sessionLinkId &&
        planProduct.product?.type === type
      );
    });

    const amount = sumProducts(products, 1, duration, incentive);
    return { products, amount };
  }, [productsAndPrices, plan, groupId, incentive, type]);

  const cellContent = useMemo(() => {
    const value = productsAndPrices.amount;
    if (value) {
      return toEuros(value);
    }
    if (!sessionLinkId) {
      return <div className={classes.cellEmpty}>Par lien</div>;
    }
    return (
      <div className={classes.cellEmpty}>
        Par défaut ({toEuros(defaultPrice.amount)})
      </div>
    );
  }, [productsAndPrices]);

  const edit = () => {
    setValue(productsAndPrices.amount / 100);
    startEditting();
  };

  const onSave = async () => {
    try {
      await updatePrice({
        type,
        amount: value ? Math.floor(value * 100) : null,
        countryPriceGroup: groupId !== NO_PRICE_GROUP ? groupId : null,
        sessionLinkId,
      });
      toast.saved();
      stopEdit();
    } catch (e) {
      toast.exception(e);
    }
  };

  if (isEditting) {
    return (
      <div className={classes.editCell}>
        <input
          type="number"
          value={value}
          className={classes.input}
          autoFocus
          onChange={(e) =>
            e.currentTarget.value
              ? setValue(Number(e.currentTarget.value))
              : setValue(undefined)
          }
        />
        <SquareButton
          icon="ri-arrow-go-back-line"
          color="ink"
          onClick={stopEdit}
          small
          smallIcon
        />
        <SquareButton
          icon="ri-check-line"
          color="green"
          onClick={onSave}
          small
          smallIcon
        />
      </div>
    );
  }

  return (
    <div className={classes.cellContent} onClick={edit}>
      <div>{cellContent}</div>
      <i className={classNames('edit-button', 'ri-edit-line', classes.icon)} />
    </div>
  );
};
